import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { RateTermsConfig } from "./ResellerRateTerms.ts";

/**
 * How a reseller is paid out their earned commission.
 *
 * - stripe_connect: payout flows through a connected Stripe account
 * - manual: paid out off-platform (wire / check / etc.), recorded by ops
 *
 * Open-ended by design — additional rails can be appended as the payout
 * project lands. Payout LOGIC is a later project; this only owns the shape.
 *
 * @since 1.0.0
 */
export const PayoutMethod = Schema.Literal("stripe_connect", "manual");
export type PayoutMethod = Schema.Schema.Type<typeof PayoutMethod>;

/**
 * Lifecycle status of the reseller's Stripe Connect (Accounts v2) connected
 * account, derived from the account's merchant capability/requirements state:
 *
 * - pending: account created, capabilities requested but not yet active (KYC /
 *   onboarding still outstanding).
 * - active: the requested capabilities (card_payments + stripe_balance.payouts)
 *   are active — the account can charge and be paid out.
 * - restricted: Stripe has restricted a capability (more information required
 *   before it can be used).
 * - disabled: a requested capability is unsupported / the account is disabled.
 *
 * @since 1.0.0
 */
export const StripeAccountStatus = Schema.Literal("pending", "active", "restricted", "disabled");
export type StripeAccountStatus = Schema.Schema.Type<typeof StripeAccountStatus>;

/**
 * Reseller profile model — reseller-only attributes kept off the customers
 * table.
 *
 * 1:1 with a customer: a row exists only for customers whose `roles[]`
 * includes `'reseller'`. This is SHAPE only — the payout LOGIC (how
 * `commissionBps` is applied to billed amounts, how/when payouts run) is a
 * later project.
 *
 * Earnings model: `commissionBps` is DEPRECATED — the legacy flat commission
 * rate in basis points (1 bps = 0.01%). It is superseded by `rateTerms`, the
 * per-reseller floor + upside-split config (default + per-customer/per-agent
 * exceptions) resolved most-specific-first at accrual time. See
 * `ResellerRateTerms.ts` for the config shape and `util/reseller-split.ts` for
 * the split formula and resolution rules.
 *
 * @since 1.0.0
 */
export class ResellerProfile extends Model.Class<ResellerProfile>("ResellerProfile")({
  id: Model.Generated(Schema.String),
  customerId: Schema.String,
  commissionBps: Schema.NullOr(Schema.Int),
  rateTerms: Schema.NullOr(RateTermsConfig),
  payoutMethod: Schema.NullOr(PayoutMethod),
  agreementUrl: Schema.NullOr(Schema.String),
  stripeAccountId: Schema.NullOr(Schema.String),
  stripeAccountStatus: Schema.NullOr(StripeAccountStatus),
  subscriptionPriceId: Schema.NullOr(Schema.String),
  signupPriceId: Schema.NullOr(Schema.String),
  // Per-reseller toggle for the (later) end-customer self-serve procurement
  // flow. SHAPE + RPC groundwork only — the flow it gates ships in a later
  // ticket. Enabling it will require a saved payment method ON the connected
  // account (a SetupIntent created with the `stripeAccount` header) and
  // `collection_method: "charge_automatically"` for that customer's
  // subscription. Defaults false; existing rows read false.
  customerSelfServeEnabled: Schema.Boolean,
  // Reseller start-invite (CLA-588). Only the sha256 hash of the plaintext
  // invite token is stored (`inviteTokenHash`); the plaintext bearer credential
  // is surfaced once at mint time and never persisted. `inviteAcceptedAt` is
  // stamped on a successful bind; the token stays valid until `inviteExpiresAt`
  // so a re-login can resume. `Schema.DateTimeUtc` (not `Model.DateTimeFromDate`)
  // so the JSON/wire form is an ISO string the decoder round-trips — the
  // Date-based variant serializes to a string the decoder then rejects.
  inviteTokenHash: Schema.NullOr(Schema.String),
  inviteSentAt: Schema.NullOr(Schema.DateTimeUtc),
  inviteExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  inviteAcceptedAt: Schema.NullOr(Schema.DateTimeUtc),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
